import React, { useState } from 'react';
import { ChevronUp, ChevronDown, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import LivePriceRight from './LivePriceRight';
import useOrderBookData from '../hooks/useOrderBookData';
import { formatPriceWithAssetDecimals } from '../utils/priceFormatter';
import { useOrderContext } from '../contexts/OrderContext';
import { useTheme } from '../contexts/ThemeContext';
import Loader from './Loader';

interface OrderBookProps {
  selectedSymbol: string;
  currentPrice: string;
  priceChange: string;
  isPricePositive: boolean;
  priceColor: string;
}

const OrderBook: React.FC<OrderBookProps> = ({
  selectedSymbol,
  currentPrice,
  priceChange,
  isPricePositive,
  priceColor
}) => {
  const [isOrderBookExpanded, setIsOrderBookExpanded] = useState(true);
  const { setSelectedPrice } = useOrderContext();
  const { theme } = useTheme();

  // Use real-time order book data
  const {
    bids,
    asks,
    spread,
    spreadPercentage,
    isLoading,
    error,
    isConnected,
    refreshData,
    reconnect
  } = useOrderBookData(selectedSymbol);

  const handlePriceClick = (price: number) => {
    setSelectedPrice(price);

  };

  // Theme-aware border color
  const borderColor = theme === 'dark' ? 'border-black' : 'border-gray-100 py-1 mb-2';

  return (
    <div className={`card p-2 pe-0 my-2  border border-gray-200 ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold theme-text-primary p-2 py-0">Order Book</h3>
        <div className="flex items-center space-x-2 me-7">
          {/* Connection Status */}
          <div className="flex items-center space-x-1">
            {isConnected ? (
              <Wifi className="h-3 w-3 text-[#2AB7A6]" />
            ) : (
              <WifiOff className="h-3 w-3 text-red-400" />
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={refreshData}
            disabled={isLoading}
            className="theme-text-quaternary hover:theme-text-secondary transition-colors duration-200 disabled:opacity-50 hidden"
            title="Refresh data"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsOrderBookExpanded(!isOrderBookExpanded)}
            className="theme-text-quaternary hover:theme-text-secondary transition-colors duration-200"
          >
            {isOrderBookExpanded ? (
              <ChevronUp className="h-4 w-4 opacity-1" />
            ) : (
              <ChevronDown className="h-4 w-4 opacity-1" />
            )}
          </button>
        </div>
      </div>

      {isOrderBookExpanded && (
        <div className="min-h-[330px] flex flex-col">
          {/* Loading State */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <Loader 
                scale='0.5x'
                size="sm" 
                title="" 
                subtitle="" 
              />
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="theme-bg-quaternary rounded-lg p-3 py-2 mx-1 text-red-400 w-full">
                <div className="flex items-center justify-between">
                  <span className="text-xs">{error}</span>
                  <button
                    onClick={reconnect}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    Reconnect
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Order Book Content */}
          {!isLoading && !error && (
            <div>
              <div className="space-y-1">
                  {/* Sell Orders (Asks) */}
                <div>
                  <h4 className="text-xs font-medium p-2 py-2 theme-text-quaternary mt-2">Sell Side (Asks)</h4>
                  <div className={`text-xs theme-text-quaternary px-2 py-1 mb-0 border-b ${borderColor}`}>
                    <div className="grid grid-cols-12 gap-1">
                      <span className="col-span-4">Price</span>
                      <span className="col-span-3 text-right">Amount</span>
                      <span className="col-span-5 text-right pr-2">Total</span>
                    </div>
                  </div>
                  <div className="max-h-20 overflow-y-auto space-y-1 scrollbar-hide">
                    {asks.slice(0, 7).map((ask: any, index: number) => (
                      <div
                        key={`ask-${index}`}
                        className="grid grid-cols-12 gap-1 text-xs cursor-pointer hover:theme-bg-quaternary rounded px-2 py-1 transition-colors duration-200 theme-text-quaternary"
                        onClick={() => handlePriceClick(ask.price)}
                      >
                        <span className="col-span-4 text-red-400 opacity-85 text-[11px]">
                          {ask.price.toFixed(2)} 
                        </span>
                        <span className="col-span-3 text-right text-[11px]">{ask.quantity.toFixed(4)}</span>
                        <span className="col-span-5 text-right pr-2 text-[11px]">{ask.total.toFixed(2)}</span>
                      </div>
                    ))}
                    {/* Show remaining orders if more than 7 exist */}
                    {asks.length > 7 && (
                      <div className="text-xs theme-text-quaternary text-center py-1 border-t theme-border hidden">
                        +{asks.length - 7} more orders (scroll to view ~200 total)
                      </div>
                    )}
                    {/* Additional orders for scrolling */}
                    {asks.slice(7).map((ask: any, index: number) => (
                      <div
                        key={`ask-${index + 7}`}
                        className="grid grid-cols-12 gap-1 text-xs cursor-pointer hover:theme-bg-quaternary rounded px-2 py-1 transition-colors duration-200 theme-text-quaternary"
                        onClick={() => handlePriceClick(ask.price)}
                      >
                        <span className="col-span-4 text-red-400 opacity-85 text-[11px]">
                          {ask.price.toFixed(2)}
                        </span>
                        <span className="col-span-3 text-right text-[11px]">{ask.quantity.toFixed(4)}</span>
                        <span className="col-span-5 text-right pr-2 text-[11px]">{ask.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

              {/* Header with Live Price - Positioned between sell and buy orders */}
              <div className="mb-4 p-0 m-0 hidden">
                <LivePriceRight
                  selectedSymbol={selectedSymbol}
                  currentPrice={currentPrice}
                  priceChange={priceChange}
                  isPricePositive={isPricePositive}
                  priceColor={priceColor}
                />
              </div>

              {/* Buy Orders (Bids) */}
              
                <div>
                  <h4 className="text-xs font-medium p-2 py-2 theme-text-quaternary mt-4">Buy Side (Bids)</h4>
                  <div className={`text-xs theme-text-quaternary px-2 py-1 mb-0 border-b ${borderColor}`}>
                    <div className="grid grid-cols-12 gap-1">
                      <span className="col-span-4">Price</span>
                      <span className="col-span-3 text-right">Amount</span>
                      <span className="col-span-5 text-right pr-2">Total</span>
                    </div>
                  </div>
                  <div className="max-h-20 overflow-y-auto space-y-1 scrollbar-hide">
                    {bids.slice(0, 7).map((bid: any, index: number) => (
                      <div
                        key={`bid-${index}`}
                        className="grid grid-cols-12 gap-1 text-xs cursor-pointer hover:theme-bg-quaternary rounded px-2 py-1 transition-colors duration-200 theme-text-quaternary"
                        onClick={() => handlePriceClick(bid.price)}
                      >
                        <span className="col-span-4 text-[#2AB7A6] text-[11px]">
                          {bid.price.toFixed(2)}
                        </span>
                        <span className="col-span-3 text-right text-[11px]">{bid.quantity.toFixed(4)}</span>
                        <span className="col-span-5 text-right pr-2 text-[11px]">{bid.total.toFixed(2)}</span>
                      </div>
                    ))}
                    {/* Show remaining orders if more than 7 exist */}
                    {bids.length > 7 && (
                      <div className="text-xs theme-text-quaternary text-center py-1 border-t theme-border hidden">
                        +{bids.length - 7} more orders (scroll to view ~200 total)
                      </div>
                    )}
                    {/* Additional orders for scrolling */}
                    {bids.slice(7).map((bid: any, index: number) => (
                      <div
                        key={`bid-${index + 7}`}
                        className="grid grid-cols-12 gap-1 text-xs cursor-pointer hover:theme-bg-quaternary rounded px-2 py-1 transition-colors duration-200 theme-text-quaternary"
                        onClick={() => handlePriceClick(bid.price)}
                      >
                        <span className="col-span-4 text-[#2AB7A6] text-[11px]">
                          {bid.price.toFixed(2)}
                        </span>
                        <span className="col-span-3 text-right text-[11px]">{bid.quantity.toFixed(4)}</span>
                        <span className="col-span-5 text-right pr-2 text-[11px]">{bid.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

              {/* Spread Info */}
              <div className="pt-1"></div>
              <div className="theme-bg-quaternary rounded-lg my-3 mb-0 p-3 py-2 mx-1 theme-text-quaternary mr-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs">Spread</span>
                  <span className="text-xs mr-0">
                    {spread > 0 ? formatPriceWithAssetDecimals(spread, selectedSymbol) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs">Spread %</span>
                  <span className="text-xs mr-0">
                    {spreadPercentage > 0 ? `${spreadPercentage.toFixed(2)}` : 'N/A'}
                  </span>
                </div>
              </div>
              
              {/* No Data State */}
              {bids.length === 0 && asks.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="theme-bg-quaternary rounded-lg p-3 py-2 mx-1 theme-text-quaternary">
                    <div className="text-center text-xs">
                      No order book data available
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderBook;
